'use client';

import type React from 'react';
import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

import { Sidebar } from './sidebar';
import { Header } from './header';
import { AskEllieSidebarDynamic } from '@/components/AskEllie/AskEllieSidebarDynamic';
import { AskEllieSidebarResizeHandle } from '@/components/AskEllie/AskEllieSidebarResizeHandle';
import { useAskEllieSidebarStore } from '@/state/askEllieSidebarState';
import { useHasMounted } from '@/hooks/useHasMounted';
import { useSidebarCollapsed } from '@/hooks/useSidebarCollapsed';
import type { BreadcrumbItem } from '@/types/navigation';
import type { NavigationConfigItem } from '@/lib/navigationConfig.types';

interface MainLayoutProps {
  children: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  documentName?: string;
  showDocumentActions?: boolean;
  navConfig?: NavigationConfigItem[];
}

export function MainLayout({
  children,
  breadcrumbs,
  documentName,
  showDocumentActions = false,
  navConfig,
}: MainLayoutProps) {
  const pathname = usePathname();
  const isOpen = useAskEllieSidebarStore((s) => s.isOpen);

  // Guard against hydration mismatch: persisted Zustand state may differ
  // between server (defaults) and client (localStorage). Only show sidebar
  // after client-side hydration completes.
  const hasMounted = useHasMounted();
  const { setIsCollapsed } = useSidebarCollapsed();

  // Collapse left nav only when Ask Ellie sidebar transitions from closed to open.
  // Using a ref to track the previous value prevents re-collapsing on every render
  // (e.g. page navigations while sidebar is open).
  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setIsCollapsed(true);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, setIsCollapsed]);

  // Auto-hide sidebar on the full-page Ask Ellie route to avoid two chat instances
  const isAskElliePage = pathname?.startsWith('/ask-ellie');
  const showSidebar = hasMounted && isOpen && !isAskElliePage;

  return (
    <div className="relative flex h-screen overflow-hidden bg-ds-surface-off-white">
      {/* The onboarding "world model" pixel texture, carried into the product
          shell. It sits behind the chrome only — the sidebar and header are
          transparent so it runs continuously underneath them, and the content
          card floats on top of it opaque. */}
      <div
        aria-hidden
        className="pixel-field pointer-events-none absolute inset-0 z-0"
      />

      {/* Sidebar - uses cached navConfig when provided */}
      <Sidebar navConfig={navConfig} />

      {/* Main Content */}
      <div className="relative z-10 flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <Header
          documentName={documentName}
          showDocumentActions={showDocumentActions}
        />

        {/* Content Area */}
        <div className="flex flex-1 overflow-hidden">
          {/* Ask Ellie manages its own scroll — use overflow-hidden so its
              internal scroll containers work correctly. Other pages scroll via main. */}
          <main
            className={`flex-1 bg-ds-surface-white border border-ds-border-subtlest rounded-lg ${isAskElliePage ? 'overflow-hidden' : 'overflow-y-auto'}`}
          >
            {children}
          </main>
        </div>
      </div>

      {/* Ask Ellie Sidebar — positioned at the top level to cover full height including header */}
      {showSidebar && (
        <>
          <AskEllieSidebarResizeHandle />
          <AskEllieSidebarDynamic />
        </>
      )}
    </div>
  );
}
